import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { type SxProps, styled } from '@mui/material/styles';
import { Button } from '@openx/components-core/lib/Button/Button';
import { noop } from '@openx/utils/noop';
import type { JSX, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { DisabledReasonMessageTooltipWrapper } from '../DisabledReasonMessageTooltip/DisabledReasonMessageTooltipWrapper';
import {
  StyledFilterDimensionTypography,
  StyledTargetingParameterLabelTypography,
} from '../TargetingReadOnly/TargetingReadOnly';

const StyledRemoveButton = styled(Button)`
  color: ${({ theme }) => theme.palette.error.main};
  margin-left: ${({ theme }) => theme.spacing(2)};

  :hover {
    color: ${({ theme }) => theme.palette.error.dark};
  }
`;

export type TargetingDropdownSectionProps = {
  name?: string | JSX.Element;
  hideButtons?: boolean;
  isEmpty?: boolean;
  isDisabled?: boolean;
  optionsCount?: number;
  disabledReasonMessage?: string;
  hasOptionsSelected?: boolean;
  onOpenDrawerClick?: (type?: string) => void;
  onRemove?: () => void;
  children?: ReactNode;
  sx?: SxProps;
};

const baseStyles = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' };

export const TargetingDropdownSection = ({
  name,
  hideButtons = false,
  isEmpty = true,
  isDisabled,
  disabledReasonMessage,
  hasOptionsSelected,
  onOpenDrawerClick = noop,
  onRemove,
  children,
  sx,
}: TargetingDropdownSectionProps) => {
  const { t } = useTranslation();
  const shouldApplyDisabledStyles = isDisabled && !hasOptionsSelected;

  if (hideButtons) return <>{children}</>;

  if (isEmpty)
    return (
      <DisabledReasonMessageTooltipWrapper
        isDisabled={shouldApplyDisabledStyles}
        disabledReasonMessage={disabledReasonMessage}
      >
        <Box
          className="targeting-dropdown-section"
          sx={{ ...baseStyles, ...sx }}
          data-test={`targeting-dropdown-section-${name}`}
        >
          <Grid container>
            <StyledTargetingParameterLabelTypography
              variant="h3"
              color={shouldApplyDisabledStyles ? 'textDisabled' : undefined}
            >
              {name}
            </StyledTargetingParameterLabelTypography>

            <StyledFilterDimensionTypography
              variant="body1"
              color={shouldApplyDisabledStyles ? 'textDisabled' : 'textSecondary'}
            >
              {t('Any')}
            </StyledFilterDimensionTypography>
          </Grid>

          {!shouldApplyDisabledStyles && (
            <Box>
              <Button
                variant="text"
                color="primary"
                type="button"
                onClick={() => onOpenDrawerClick()}
                data-test="add-button"
              >
                {t('Add')}
              </Button>
            </Box>
          )}
        </Box>
      </DisabledReasonMessageTooltipWrapper>
    );

  return (
    <Box
      className="targeting-dropdown-section"
      sx={{ ...baseStyles, ...sx }}
      data-test={`targeting-dropdown-section-${name}`}
    >
      <Box sx={{ mt: -2, flex: 1 }}>{children}</Box>
      <Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>
        {!isDisabled && (
          <Button
            variant="text"
            color="primary"
            type="button"
            onClick={() => onOpenDrawerClick()}
            data-test="edit-button"
          >
            {t('Edit')}
          </Button>
        )}
        {onRemove && (
          <StyledRemoveButton
            variant="text"
            color="secondary"
            type="button"
            onClick={onRemove}
            data-test="remove-button"
          >
            {t('Remove')}
          </StyledRemoveButton>
        )}
      </Box>
    </Box>
  );
};
